<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormAudioguide
          :data="audioguide"
          :cities="cities"
          :loading="isFormLoading"
          :disabled="isMutationLoading"
          :isNew="!id"
          :dictionaries="dictionaries"
          :language="language"
          @delete-audioguide="deleteAudioguide"
          @submit="audioguideSubmitHandle"
          @cancel="$router.push({ name: 'page-audioguides' })"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmAudioguideRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import FormAudioguide from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'
import audioguideQuery from '@/services/gql/types/Audioguides/queries/audioguide.gql'
import createAudioguideMutation from '@/services/gql/types/Audioguides/mutations/createAudioguide.gql'
import deleteAudioguideMutation from '@/services/gql/types/Audioguides/mutations/deleteAudioguide.gql'
import updateAudioguideMutation from '@/services/gql/types/Audioguides/mutations/updateAudioguide.gql'
import updateAudioguidePointMutation from '@/services/gql/types/AudioguidePoints/mutations/updateAudioguidePoint.gql'
import createAudioguidePointMutation from '@/services/gql/types/AudioguidePoints/mutations/createAudioguidePoint.gql'
import deleteAudioguidePointMutation from '@/services/gql/types/AudioguidePoints/mutations/deleteAudioguidePoint.gql'
import dictionariesQuery from '@/services/gql/types/Dictionaries/queries/dictionaries.gql'
import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

export default {
  name: 'AudioguideAudioguide',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormAudioguide,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать аудиогид' : 'Создать аудиогид'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      audioguide: {},
      dictionaries: [],
      cities: null
    }
  },
  computed: {
    isFormLoading () {
      return (
        this.$apollo.queries.audioguide.loading
      )
    },
    isMutationLoading () {
      return this.$async.createAudioguide.$pending ||
        this.$async.updateAudioguide.$pending ||
        this.$async.updateAudioguidePoint.$pending ||
        this.$async.createAudioguidePoint.$pending
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async refetchAudioguide () {
      return await this.$apollo.queries.audioguide.refetch()
    },
    async deleteAudioguide () {
      try {
        await this.$refs.confirmAudioguideRemoving.open()
        await this.$async.deleteAudioguide.$perform({ id: this.id })
        this.$successMsg('Аудиогид удален')
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении аудиогида с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-audioguides' })
      }
    },
    audioguideSubmitHandle (audioguidePayload) {
      if (this.id) this.updateAudioguide(audioguidePayload)
      else this.createAudioguide(audioguidePayload)
    },
    async createAudioguidePoint (audioguideId, pointData) {
      delete pointData.id
      delete pointData.translations
      return this.$async.createAudioguidePoint.$perform({
        data: {
          ...pointData,
          audioguideId
        }
      })
    },
    async createAudioguide (audioguidePayload) {
      try {
        delete audioguidePayload.data.translations
        let points = audioguidePayload.data.points
        delete audioguidePayload.data.points

        const {
          data: { createAudioguide }
        } = await this.$async.createAudioguide.$perform({
          data: audioguidePayload.data
        })
        points = points.map((point, pointIndex) => {
          point.data.sortOrder = pointIndex
          return point
        })
        const promises = []
        for (const point of points) {
          if (createAudioguide.id) {
            if (createAudioguide.id) {
              promises.push(this.changeAudioguidePoint(createAudioguide.id, point.data))
            }
          }
        }
        await Promise.all(promises)
        this.$successMsg('Аудиогид создан')
        this.$emit('updated')
        await this.$router.push({ name: 'page-audioguides' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании аудиогида')
      }
    },
    async changeAudioguidePoint (audioguideId, pointData) {
      if (pointData.id) {
        return this.updateAudioguidePoint(audioguideId, pointData)
      } else {
        return this.createAudioguidePoint(audioguideId, pointData)
      }
    },
    async removeAudioguidePoint (id) {
      try {
        await this.$async.deleteAudioguidePoint.$perform({ id })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при удалении точки')
      }
    },
    async updateAudioguidePoint (audioguideId, pointData) {
      const id = pointData.id
      delete pointData.id
      setTranslation(pointData, this.language)
      await this.$async.updateAudioguidePoint.$perform(
        {
          id,
          data: {
            ...pointData,
            audioguideId
          }
        },
        { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
      )
    },
    async updateAudioguide (audioguidePayload) {
      const audioguideId = Number(this.id)
      const points = [...audioguidePayload.data.points]
      const sortPoints = points.map((point, pointIndex) => {
        point.data.sortOrder = pointIndex
        return point
      })
      delete audioguidePayload.data.points
      const promises = []
      try {
        setTranslation(audioguidePayload.data, this.language)
        // update audioguide
        promises.push(
          this.$async.updateAudioguide.$perform(
            {
              id: audioguideId,
              data: audioguidePayload.data
            },
            {
              Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
            }
          )
        )
        // update points
        for (const point of sortPoints) {
          if (audioguideId) {
            promises.push(this.changeAudioguidePoint(audioguideId, point.data))
          }
        }
        // delete points
        for (const pointToRemove of audioguidePayload.postActions.itemsToRemove) {
          if (pointToRemove) {
            promises.push(this.removeAudioguidePoint(pointToRemove))
          }
        }
        promises.push(this.refetchAudioguide())
        await Promise.all(promises)
        this.$successMsg('Аудиогид обновлен')
        await this.$apollo.queries.audioguide.refetch()
        this.$emit('updated')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при обновлении аудиогида')
      }
    }
  },
  apollo: {
    audioguide: {
      fetchPolicy: 'network-only',
      query: audioguideQuery,
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-audioguides' })
        }
        this.audioguide = {
          ...res?.data?.audioguide,
          cityId: res?.data?.audioguide?.city?.id || null
        }
      }
    },
    dictionaries: {
      fetchPolicy: 'network-only',
      query: dictionariesQuery,
      variables () {
        return { where: {} }
      },
      result ({ data }) {
        this.dictionaries = data.dictionaries.list
      }
    },
    cities: {
      fetchPolicy: 'network-only',
      query: citiesQuery,
      pollInterval: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result (res) {
        this.cities = res?.data?.cities?.list?.map(city => ({
          id: city.id,
          title: city.title
        }))
      }
    }
  },
  async: {
    deleteAudioguide (variables) {
      return this.$apollo.mutate({
        mutation: deleteAudioguideMutation,
        variables
      })
    },
    createAudioguide (variables) {
      return this.$apollo.mutate({
        mutation: createAudioguideMutation,
        variables
      })
    },
    updateAudioguide (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateAudioguideMutation,
        variables,
        context: {
          headers
        }
      })
    },
    deleteAudioguidePoint (variables) {
      return this.$apollo.mutate({
        mutation: deleteAudioguidePointMutation,
        variables
      })
    },
    createAudioguidePoint (variables) {
      return this.$apollo.mutate({
        mutation: createAudioguidePointMutation,
        variables
      })
    },
    updateAudioguidePoint (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateAudioguidePointMutation,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
